import React, { useState, useEffect, useCallback } from 'react';
import API_BASE_URL from '../config';

const fontStack = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

function TrainingProgress({ channelUrl, customerKey, videoIds = [], onComplete }) {
  const [trainingStatus, setTrainingStatus] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [showCompletionNotification, setShowCompletionNotification] = useState(false);

  const fetchTrainingStatus = useCallback(async () => {
    if (!channelUrl || !customerKey) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/training_status?channel_url=${encodeURIComponent(channelUrl)}&customer_key=${encodeURIComponent(customerKey)}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch training status: ${response.statusText}`);
      }

      const data = await response.json();
      setTrainingStatus(data);
      setLastUpdate(new Date());
      setError(null);

      if (data.is_training_active === false && isPolling) {
        setIsPolling(false);
        setShowCompletionNotification(true);
        setTimeout(() => setShowCompletionNotification(false), 5000);
        if (onComplete) onComplete(data);
      }
    } catch (err) {
      console.error('Error fetching training status:', err);
      setError(err.message);
    }
  }, [channelUrl, customerKey, isPolling, onComplete]);

  useEffect(() => {
    if (videoIds && videoIds.length > 0) {
      setIsPolling(true);
      setShowCompletionNotification(false);
    }
  }, [videoIds]);

  useEffect(() => {
    if (!isPolling) return;
    fetchTrainingStatus();
    const interval = setInterval(fetchTrainingStatus, 3000);
    return () => clearInterval(interval);
  }, [isPolling, fetchTrainingStatus]);

  const getProgressPercentage = () => {
    if (!trainingStatus || trainingStatus.total_videos === 0) return 0;
    return Math.round((trainingStatus.completed_count / trainingStatus.total_videos) * 100);
  };

  const statusColors = {
    completed: '#22c55e',
    processing: '#3b82f6',
    failed: '#ef4444',
    pending: '#f59e0b',
  };

  const statusIcons = {
    completed: '\u2713',
    processing: '\u21BB',
    failed: '\u2717',
    pending: '\u25CB',
  };

  if (!trainingStatus && !isPolling) return null;

  return (
    <div style={{
      marginTop: '24px',
      padding: '24px',
      backgroundColor: '#fafafa',
      borderRadius: '12px',
      border: '1px solid #eee',
      fontFamily: fontStack,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#1a1a1a' }}>
          Training Progress
          {isPolling && (
            <span style={{ marginLeft: '8px', fontSize: '0.75rem', color: '#3b82f6', animation: 'pulse 1.5s infinite' }}>
              Live
            </span>
          )}
        </h3>
        <button
          onClick={() => fetchTrainingStatus()}
          style={{
            padding: '5px 12px',
            fontSize: '0.8rem',
            fontFamily: fontStack,
            fontWeight: 500,
            backgroundColor: '#111',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
          }}
        >
          Refresh
        </button>
      </div>

      {/* Completion */}
      {showCompletionNotification && (
        <div style={{
          padding: '10px 14px',
          marginBottom: '14px',
          backgroundColor: '#f0fdf4',
          color: '#166534',
          borderRadius: '8px',
          border: '1px solid #bbf7d0',
          textAlign: 'center',
          fontSize: '0.875rem',
          fontWeight: 500,
        }}>
          Training completed successfully
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{
          padding: '10px 14px',
          marginBottom: '14px',
          backgroundColor: '#fef2f2',
          color: '#991b1b',
          borderRadius: '8px',
          border: '1px solid #fecaca',
          fontSize: '0.85rem',
        }}>
          {error}
        </div>
      )}

      {trainingStatus && (
        <>
          {/* Progress bar */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '0.8rem', color: '#888' }}>
              <span>Overall</span>
              <span style={{ fontWeight: 600, color: '#1a1a1a' }}>
                {trainingStatus.completed_count} / {trainingStatus.total_videos}
              </span>
            </div>
            <div style={{ width: '100%', height: '8px', backgroundColor: '#e5e5e5', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{
                width: `${getProgressPercentage()}%`,
                height: '100%',
                backgroundColor: '#111',
                borderRadius: '4px',
                transition: 'width 0.5s ease-out',
              }} />
            </div>
          </div>

          {/* Status grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '20px' }}>
            {[
              { label: 'Processing', count: trainingStatus.processing_count, color: statusColors.processing },
              { label: 'Completed', count: trainingStatus.completed_count, color: statusColors.completed },
              { label: 'Failed', count: trainingStatus.failed_count, color: statusColors.failed },
              { label: 'Pending', count: trainingStatus.pending_count, color: statusColors.pending },
            ].map(({ label, count, color }) => (
              <div key={label} style={{
                padding: '10px',
                backgroundColor: '#fff',
                borderRadius: '8px',
                textAlign: 'center',
                border: '1px solid #eee',
              }}>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, color }}>{count}</div>
                <div style={{ fontSize: '0.7rem', color: '#888', marginTop: '2px' }}>{label}</div>
              </div>
            ))}
          </div>

          {/* Video details */}
          {(trainingStatus.videos_by_status.processing.length > 0 ||
            trainingStatus.videos_by_status.failed.length > 0 ||
            trainingStatus.videos_by_status.pending.length > 0) && (
            <div>
              <h4 style={{ margin: '0 0 10px 0', fontSize: '0.9rem', fontWeight: 600, color: '#1a1a1a' }}>
                Details
              </h4>
              <div style={{
                maxHeight: '260px',
                overflowY: 'auto',
                backgroundColor: '#fff',
                borderRadius: '8px',
                border: '1px solid #eee',
              }}>
                {['processing', 'failed', 'pending'].flatMap(status =>
                  (trainingStatus.videos_by_status[status] || []).map((video, idx) => (
                    <div
                      key={`${status}-${idx}`}
                      style={{
                        padding: '10px 14px',
                        borderBottom: '1px solid #f5f5f5',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        background: status === 'failed' ? '#fef2f2' : 'transparent',
                      }}
                    >
                      <span style={{
                        width: '22px',
                        height: '22px',
                        borderRadius: '50%',
                        backgroundColor: statusColors[status],
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        flexShrink: 0,
                        animation: status === 'processing' ? 'spin 2s linear infinite' : 'none',
                      }}>
                        {statusIcons[status]}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {video.video_title}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#999', marginTop: '1px' }}>
                          {video.video_id}
                        </div>
                        {video.error_message && (
                          <div style={{ fontSize: '0.7rem', color: '#dc2626', marginTop: '2px' }}>
                            {video.error_message}
                          </div>
                        )}
                      </div>
                      <span style={{ fontSize: '0.75rem', fontWeight: 500, color: statusColors[status], flexShrink: 0 }}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                        {status === 'processing' && '...'}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Last update */}
          {lastUpdate && (
            <div style={{ marginTop: '12px', fontSize: '0.7rem', color: '#bbb', textAlign: 'right' }}>
              Updated {lastUpdate.toLocaleTimeString()}
            </div>
          )}
        </>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default TrainingProgress;
